import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Logo3D from '@/components/Logo3D';
import { Mail, Lock, Smartphone, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// OTP Input Component
const OTPInput = ({ length = 6, value, onChange, disabled }) => {
  const inputRefs = useRef([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, e) => {
    const val = e.target.value;
    if (!/^\d*$/.test(val)) return; // Only allow digits

    const newValue = value.split('');
    newValue[index] = val.slice(-1); // Take only last digit
    const newOtp = newValue.join('');
    onChange(newOtp);

    // Auto-focus next input
    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pastedData);
    const focusIndex = Math.min(pastedData.length, length - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className="flex justify-center gap-2 sm:gap-3">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#FF9933] focus:ring-2 focus:ring-orange-200 focus:outline-none transition-all disabled:bg-gray-100"
          data-testid={`otp-input-${index}`}
        />
      ))}
    </div>
  );
};

export default function AuthPage({ setUser }) {
  const [authMode, setAuthMode] = useState('login'); // login, signup, otp-send, otp-verify
  const [loginMethod, setLoginMethod] = useState('password'); // password, otp
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Auto-verify when OTP is complete
  useEffect(() => {
    if (otp.length === 6 && otpSent) {
      handleVerifyOTP();
    }
  }, [otp]);

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const isLogin = authMode === 'login';

      // ── SIGNUP: gate behind email OTP verification ───────────────────────
      if (!isLogin) {
        // Step 1: send OTP to the entered email — user must prove ownership
        await axios.post(`${API}/auth/send-otp`, { email: formData.email });
        setOtpEmail(formData.email);
        setOtpSent(true);
        // Persist the signup form fields so we can complete signup after verify
        localStorage.setItem('dharmbook_pending_signup', JSON.stringify(formData));
        toast.success(`OTP sent to ${formData.email}. Please check your inbox (and spam).`);
        setLoading(false);
        return;
      }

      // ── LOGIN (password) ─────────────────────────────────────────────────
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email, password: formData.password,
      });
      const { token, user } = response.data;

      localStorage.setItem('dharmbook_token', token);
      localStorage.setItem('dharmbook_user', JSON.stringify(user));

      setUser(user);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e?.preventDefault();
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, {
        email: formData.email
      });

      setOtpEmail(formData.email);
      setOtpSent(true);
      setResendTimer(60); // 60 seconds before resend
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter complete 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      // If there's a pending signup, complete signup with the OTP code
      const pendingRaw = localStorage.getItem('dharmbook_pending_signup');
      if (pendingRaw) {
        const pending = JSON.parse(pendingRaw);
        const signupResp = await axios.post(`${API}/auth/signup`, {
          name: pending.name,
          email: pending.email,
          password: pending.password,
          role: pending.role || 'user',
          avatar: pending.avatar,
          otp_code: otp,
        });
        const { token, user } = signupResp.data;
        localStorage.setItem('dharmbook_token', token);
        localStorage.setItem('dharmbook_user', JSON.stringify(user));
        localStorage.removeItem('dharmbook_pending_signup');
        setUser(user);
        toast.success('Welcome to DharmBook!');
        navigate('/');
        return;
      }

      // Else: OTP login flow (existing behavior)
      const response = await axios.post(`${API}/auth/verify-otp`, {
        email: otpEmail,
        otp: otp,
      });

      const { token, user } = response.data;

      localStorage.setItem('dharmbook_token', token);
      localStorage.setItem('dharmbook_user', JSON.stringify(user));

      setUser(user);
      toast.success('Welcome to DharmBook!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtp(''); // Clear OTP on error
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-otp`, { email: otpEmail });
      setResendTimer(60);
      setOtp('');
      toast.success('New OTP sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const resetToLogin = () => {
    setOtpSent(false);
    setOtp('');
    setOtpEmail('');
    setLoginMethod('password');
  };

  // OTP Verification Screen
  if (otpSent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-gray-50 via-orange-50 to-amber-50">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 peaceful-enter">
            <Logo3D size="xl" showText={true} />
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 peaceful-enter">
            {/* Back Button */}
            <button
              onClick={resetToLogin}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to login</span>
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h2>
              <p className="text-gray-600 text-sm">
                We've sent a 6-digit code to<br />
                <span className="font-semibold text-gray-900">{otpEmail}</span>
              </p>
            </div>

            {/* OTP Input */}
            <div className="mb-6">
              <OTPInput
                length={6}
                value={otp}
                onChange={setOtp}
                disabled={loading}
              />
            </div>

            {/* Verify Button */}
            <Button
              onClick={handleVerifyOTP}
              disabled={loading || otp.length !== 6}
              data-testid="verify-otp-btn"
              className="w-full rounded-full bg-[#FF9933] hover:bg-[#E68A00] text-white font-semibold py-5 shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verifying...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Verify & Continue
                </span>
              )}
            </Button>

            {/* Resend OTP */}
            <div className="text-center mt-6">
              <p className="text-sm text-gray-600 mb-2">Didn't receive the code?</p>
              {resendTimer > 0 ? (
                <p className="text-sm text-gray-500">
                  Resend in <span className="font-semibold text-[#FF9933]">{resendTimer}s</span>
                </p>
              ) : (
                <button
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="text-[#FF9933] font-semibold hover:underline disabled:opacity-50"
                  data-testid="resend-otp-btn"
                >
                  Resend OTP
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Login/Signup Screen
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-gray-50 via-orange-50 to-amber-50">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 peaceful-enter">
          <Logo3D size="xl" showText={true} />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 peaceful-enter">
          {/* Login/Signup Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              data-testid="login-tab"
              onClick={() => { setAuthMode('login'); setLoginMethod('password'); }}
              variant={authMode === 'login' ? 'default' : 'ghost'}
              className={`flex-1 rounded-full ${authMode === 'login' ? 'bg-[#FF9933] hover:bg-[#E68A00]' : ''}`}
            >
              Login
            </Button>
            <Button
              data-testid="signup-tab"
              onClick={() => { setAuthMode('signup'); setLoginMethod('password'); }}
              variant={authMode === 'signup' ? 'default' : 'ghost'}
              className={`flex-1 rounded-full ${authMode === 'signup' ? 'bg-[#FF9933] hover:bg-[#E68A00]' : ''}`}
            >
              Sign Up
            </Button>
          </div>

          {/* Google Sign-In */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            data-testid="google-signin-btn"
            className="w-full flex items-center justify-center gap-3 py-3 mb-4 rounded-full border-2 border-stone-200 bg-white hover:bg-gray-50 hover:border-stone-300 font-semibold text-gray-700 transition-all shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-xs text-gray-400 font-medium">OR</span>
            <div className="flex-1 h-px bg-stone-200" />
          </div>

          {/* Login Method Toggle (only for login) */}
          {authMode === 'login' && (
            <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-xl">
              <button
                onClick={() => setLoginMethod('password')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
                  loginMethod === 'password' 
                    ? 'bg-white shadow-sm text-gray-900' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="password-method-btn"
              >
                <Lock className="w-4 h-4" />
                Password
              </button>
              <button
                onClick={() => setLoginMethod('otp')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
                  loginMethod === 'otp' 
                    ? 'bg-white shadow-sm text-gray-900' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="otp-method-btn"
              >
                <Smartphone className="w-4 h-4" />
                OTP Login
              </button>
            </div>
          )}

          {/* Password Login/Signup Form */}
          {loginMethod === 'password' && (
            <form onSubmit={handlePasswordAuth} className="space-y-4">
              {authMode === 'signup' && (
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required={authMode === 'signup'}
                    className="rounded-xl border-stone-200"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="rounded-xl border-stone-200"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="rounded-xl border-stone-200"
                />
                {authMode === 'login' && (
                  <div className="mt-1 text-right">
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-[#FF9933] hover:text-[#E68A00] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                )}
              </div>

              {authMode === 'signup' && (
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="role-select" className="rounded-xl border-stone-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Devotee</SelectItem>
                      <SelectItem value="pandit">Pandit Ji</SelectItem>
                      <SelectItem value="creator">Creator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                type="submit"
                data-testid="auth-submit-btn"
                disabled={loading}
                className="w-full rounded-full bg-[#FF9933] hover:bg-[#E68A00] text-white font-semibold py-5 shadow-md hover:-translate-y-0.5 transition-all"
              >
                {loading ? 'Please wait...' : authMode === 'login' ? 'Login' : 'Sign Up'}
              </Button>
            </form>
          )}

          {/* OTP Login Form */}
          {loginMethod === 'otp' && authMode === 'login' && (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div className="text-center mb-4">
                <p className="text-sm text-gray-600">
                  We'll send a 6-digit code to your email for secure, passwordless login.
                </p>
              </div>

              <div>
                <Label htmlFor="otp-email">Email</Label>
                <Input
                  id="otp-email"
                  data-testid="otp-email-input"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="rounded-xl border-stone-200"
                />
              </div>

              <Button
                type="submit"
                data-testid="send-otp-btn"
                disabled={loading}
                className="w-full rounded-full bg-[#FF9933] hover:bg-[#E68A00] text-white font-semibold py-5 shadow-md hover:-translate-y-0.5 transition-all"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending OTP...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Send OTP
                  </span>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500 mt-4">
                New user? An account will be created automatically.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
